import { FunctionComponent, useContext, useMemo } from 'react';

import { PoweredByAssemblyBranding } from 'src/components/icons/PoweredByAssemblyBranding';
import { PortalConfigContext, useFeatureFlags } from 'src/context';
import {
  BadgePlacement,
  getPoweredByBadgeUrl,
} from 'src/features/badge/poweredByBadgeUrl';
import { useGetPortalPlanQuery } from 'src/services/api';
import { canDisablePortalBadge } from 'src/utils/PlansUtils';

export type BadgeVariant = 'bar' | 'corner';

// Sizes and offsets are measured from the Figma frames. Tailwind preflight is
// off in this app, so padding adds to width — the bar stretches as a flex child
// instead of taking w-full, which would push the mark past the card edge. The
// bar tucks 12px under the card content (and pads it back) so the content's
// rounded bottom corners show the bar's grey rather than the card's white.
const VARIANT_CLASS_NAMES: Record<
  BadgeVariant,
  { container: string; mark: string }
> = {
  bar: {
    container:
      'flex justify-end bg-gray-150 -mt-3 pt-[29px] pb-[18px] pr-[14px]',
    mark: 'block h-4 w-[168px]',
  },
  corner: {
    container:
      'absolute bottom-0 right-0 z-10 rounded-tl-xl bg-gray-150 pt-[15px] pb-[14px] pl-[9px] pr-2',
    mark: 'block h-[15px] w-[158px]',
  },
};

interface Props {
  isAuthScreen: boolean;
  placement: BadgePlacement;
  variant?: BadgeVariant;
}

export const PoweredByAssemblyBadge: FunctionComponent<Props> = ({
  isAuthScreen,
  placement,
  variant,
}) => {
  const portalConfig = useContext(PortalConfigContext);
  const { EnableGrowthLoops } = useFeatureFlags();
  const { data: portalPlanData, isSuccess } = useGetPortalPlanQuery({
    isAuthScreen,
  });

  /**
   * This method check for portal plan status. It call
   * get portal plan api which return if the portal is in pro
   * plan or not. And then, it updates the portal badge render condition
   * When it is a pro plan, get show badge flag prop from
   * the portal config, otherwise it should be explicitly shown.
   */
  const shouldShowPortalBadge = useMemo(() => {
    if (isSuccess) {
      const { isPro, isLegacyPaidPlan, isStudioPlan, planType } =
        portalPlanData;
      const canDisableBadge = canDisablePortalBadge({
        isPro,
        isLegacyPaidPlan,
        isStudioPlan,
        planType,
      });
      return canDisableBadge ? !portalConfig.MarketingSite.disableBadge : true;
    }
    return false;
  }, [portalPlanData, isSuccess]);

  if (!shouldShowPortalBadge) return null;

  const href = getPoweredByBadgeUrl({
    placement,
    portalId: portalConfig.portalHeader,
    workspaceName: portalConfig.name,
    currentUrl: window.location.href,
    enableGrowthLoops: EnableGrowthLoops,
  });

  if (variant) {
    const classNames = VARIANT_CLASS_NAMES[variant];
    return (
      <div className={classNames.container}>
        <a href={href} target="_blank" className="block" rel="noreferrer">
          <PoweredByAssemblyBranding className={classNames.mark} />
        </a>
      </div>
    );
  }

  return (
    <div className="group pr-4 z-10 relative rounded mt-6">
      <a
        href={href}
        target="_blank"
        className="flex items-center gap-2 justify-center no-underline"
        rel="noreferrer"
      >
        <PoweredByAssemblyBranding className="text-gray-small group-hover:text-black-heading h-3.5" />
      </a>
    </div>
  );
};
